import React, { useState, useRef, useEffect, useId } from 'react';
import { getMediaUrl } from '../utils/mediaUrl';
import { claimMediaPlayback, onMediaPlaybackClaim } from '../utils/mediaPlayback';
import { getCachedBlobUrlSync, getCachedMedia, storeMediaInCache } from '../utils/mediaCache';

const SPEEDS = [1, 1.25, 1.5, 2, 0.5];

// A tiny silent clip. iOS only allows audio that was started inside the tap,
// so this primes the element before the note finishes downloading.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

const isAppleTouch = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Keep finished downloads in memory so a second tap plays immediately.
const downloadedAudio = new Map();

const rememberDownload = (key, url) => {
  if (downloadedAudio.has(key)) downloadedAudio.delete(key);
  downloadedAudio.set(key, url);
  while (downloadedAudio.size > 32) {
    const oldest = downloadedAudio.keys().next().value;
    const oldUrl = downloadedAudio.get(oldest);
    downloadedAudio.delete(oldest);
    if (typeof oldUrl === 'string' && oldUrl.startsWith('blob:') && !oldUrl.includes('#persisted')) {
      // Memory cleanup
    }
  }
};

const typeFromUrl = (url) => {
  const path = (url || '').split('?')[0].toLowerCase();
  if (path.endsWith('.webm')) return 'audio/webm';
  if (path.endsWith('.ogg')) return 'audio/ogg';
  if (path.endsWith('.mp3')) return 'audio/mpeg';
  if (path.endsWith('.m4a') || path.endsWith('.mp4')) return 'audio/mp4';
  if (path.endsWith('.wav')) return 'audio/wav';
  if (path.endsWith('.aac')) return 'audio/aac';
  return '';
};

const downloadAudio = async (url, onProgress, signal) => {
  // 1. Check in-memory session cache or mediaCache sync pool
  const cachedMem = downloadedAudio.get(url) || getCachedBlobUrlSync(url);
  if (cachedMem) {
    onProgress(1);
    return cachedMem;
  }
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    onProgress(1);
    return url;
  }

  // 2. Check persistent on-device IndexedDB storage
  const cachedDevice = await getCachedMedia(url);
  if (cachedDevice?.blobUrl) {
    rememberDownload(url, cachedDevice.blobUrl);
    onProgress(1);
    return cachedDevice.blobUrl;
  }

  // 3. Download from network with credentials omitted
  const response = await fetch(url, { signal, credentials: 'omit' });
  if (!response.ok) throw new Error(`Audio download failed (${response.status})`);

  const headerType = response.headers.get('content-type') || '';
  const mime = headerType.startsWith('audio/') || headerType.startsWith('video/')
    ? headerType
    : (typeFromUrl(url) || headerType || 'audio/webm');
  const total = Number(response.headers.get('content-length')) || 0;
  const reader = response.body && response.body.getReader ? response.body.getReader() : null;

  let blob;
  if (!reader) {
    blob = await response.blob();
    onProgress(1);
  } else {
    const chunks = [];
    let received = 0;
    let lastPaint = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      const now = Date.now();
      if (total > 0 && (now - lastPaint > 80 || received >= total)) {
        lastPaint = now;
        onProgress(Math.min(1, received / total));
      } else if (!total && now - lastPaint > 80) {
        lastPaint = now;
        onProgress(null);
      }
    }
    onProgress(1);
    blob = new Blob(chunks, { type: mime });
  }

  // 4. Save to persistent on-device media storage for instant playback
  const objectUrl = await storeMediaInCache(url, blob, mime);
  rememberDownload(url, objectUrl);
  return objectUrl;
};

const AudioPlayer = ({ src, duration: initialDuration, variant = 'default', isUploading = false, uploadProgress = null }) => {
  const audioRef = useRef(null);
  const abortRef = useRef(null);
  const requestRef = useRef(0);
  const reactId = useId();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);

  const resolvedSrc = getMediaUrl(src);
  const ownerId = `audio:${resolvedSrc || reactId}`;

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, [resolvedSrc]);

  useEffect(() => {
    return onMediaPlaybackClaim(ownerId, () => {
      requestRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
      setIsLoading(false);
      setLoadProgress(0);
      const audio = audioRef.current;
      if (audio && !audio.paused) audio.pause();
      setIsPlaying(false);
    });
  }, [ownerId]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.defaultPlaybackRate = 1.0;
      audioRef.current.playbackRate = SPEEDS[speedIndex];
    }
  }, [resolvedSrc, speedIndex]);

  const bars = React.useMemo(() => {
    const count = variant.startsWith('chat') ? 24 : 30;
    const str = src || 'bazzef_voice';
    const result = [];
    for (let i = 0; i < count; i++) {
      const charCode = str.charCodeAt(i % str.length);
      const val = 25 + ((charCode * (i + 1) * 17) % 75);
      result.push(val);
    }
    return result;
  }, [src, variant]);

  const stopLoading = () => {
    requestRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setIsLoading(false);
    setLoadProgress(0);
  };

  const playSource = (url) => {
    const audio = audioRef.current;
    if (!audio) return Promise.reject(new Error('No audio element'));
    claimMediaPlayback(ownerId);
    // Invalidate a silent prime so its pause() cannot stop the real note.
    audio._primeGen = (audio._primeGen || 0) + 1;
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    if (audio.src !== url) audio.src = url;
    audio.playbackRate = SPEEDS[speedIndex];
    return audio.play();
  };

  // Must run in the tap, before any await. iOS ignores play() that starts later.
  const primeAppleAudio = () => {
    if (!isAppleTouch()) return;
    const audio = audioRef.current;
    if (!audio) return;
    const gen = (audio._primeGen || 0) + 1;
    audio._primeGen = gen;
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    audio.src = SILENT_WAV;
    const started = audio.play();
    if (started && started.then) {
      started.then(() => {
        if (audio._primeGen !== gen) return;
        audio.pause();
      }).catch(() => {});
    }
  };

  const startPlayback = async () => {
    if (isUploading || isLoading || !resolvedSrc) return;
    claimMediaPlayback(ownerId);
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const alreadyLocal = downloadedAudio.has(resolvedSrc)
      || resolvedSrc.startsWith('blob:')
      || resolvedSrc.startsWith('data:');
    if (alreadyLocal) {
      const localUrl = downloadedAudio.get(resolvedSrc) || resolvedSrc;
      playSource(localUrl).catch(console.warn);
      return;
    }

    primeAppleAudio();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setLoadProgress(0);
    try {
      const objectUrl = await downloadAudio(
        resolvedSrc,
        (fraction) => {
          if (requestRef.current === requestId) setLoadProgress(fraction);
        },
        controller.signal
      );
      if (requestRef.current !== requestId) return;
      setIsLoading(false);
      setLoadProgress(1);
      try {
        await playSource(objectUrl);
      } catch (playErr) {
        if (requestRef.current !== requestId) return;
        // Blob playback can be rejected on iOS. The element is already
        // unlocked, so the remote file can still start.
        await playSource(resolvedSrc);
        if (playErr) console.warn(playErr);
      }
    } catch (err) {
      if (err?.name === 'AbortError' || requestRef.current !== requestId) return;
      console.warn(err);
      setIsLoading(false);
      setLoadProgress(0);
      try {
        await playSource(resolvedSrc);
      } catch (playErr) {
        console.warn(playErr);
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (isUploading) return;
    if (isLoading) {
      stopLoading();
      return;
    }
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      return;
    }
    const readyUrl = downloadedAudio.get(resolvedSrc);
    if (readyUrl && audioRef.current.src === readyUrl && audioRef.current.readyState >= 2) {
      claimMediaPlayback(ownerId);
      audioRef.current.playbackRate = SPEEDS[speedIndex];
      audioRef.current.play().catch(console.warn);
      return;
    }
    startPlayback();
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[speedIndex];
      if (isFinite(audioRef.current.duration) && audioRef.current.duration > 0) {
        setDuration(audioRef.current.duration);
      }
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSpeedToggle = (e) => {
    e.stopPropagation();
    const nextIndex = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(nextIndex);
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEEDS[nextIndex];
    }
  };

  const handleScrub = (e, index) => {
    e.stopPropagation();
    if (isLoading || !audioRef.current || !audioRef.current.src || !duration) return;
    const fraction = (index + 0.5) / bars.length;
    const targetTime = fraction * duration;
    audioRef.current.currentTime = targetTime;
    setCurrentTime(targetTime);
    if (!isPlaying) {
      claimMediaPlayback(ownerId);
      audioRef.current.playbackRate = SPEEDS[speedIndex];
      audioRef.current.play().catch(console.warn);
    }
  };

  const formatTime = (secs) => {
    if (typeof secs !== 'number' || !isFinite(secs) || isNaN(secs) || secs <= 0) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const validDuration = isFinite(duration) && duration > 0 ? duration : (initialDuration && isFinite(initialDuration) && initialDuration > 0 ? initialDuration : 0);
  const downloadFraction = typeof loadProgress === 'number' ? loadProgress : 0;
  const progressFraction = isLoading
    ? downloadFraction
    : (validDuration > 0 ? currentTime / validDuration : 0);

  const isChatMine = variant === 'chat-mine';
  const isChatOther = variant === 'chat-other';
  const isChat = isChatMine || isChatOther;

  const containerClasses = isChat
    ? 'w-[230px] sm:w-[260px] md:w-[280px] flex items-center gap-2.5 p-1 select-none'
    : 'my-2.5 max-w-md w-full flex items-center gap-3 p-3 bg-zinc-100/90 dark:bg-zinc-800/80 border border-zinc-200/80 dark:border-zinc-700/60 rounded-2xl shadow-xs backdrop-blur-xs select-none';

  const playBtnClasses = isChatMine
    ? 'w-7 h-7 rounded-full bg-white text-indigo-700 hover:bg-white/95 flex items-center justify-center shrink-0 shadow-2xs active:scale-95 transition-transform'
    : isChatOther
    ? 'w-7 h-7 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 flex items-center justify-center shrink-0 shadow-2xs active:scale-95 transition-transform'
    : 'w-10 h-10 rounded-full bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-white active:scale-95 text-white dark:text-zinc-900 flex items-center justify-center shadow-xs shrink-0 transition-all';

  const activeBarClass = isChatMine
    ? 'bg-white'
    : isChatOther
    ? 'bg-zinc-900 dark:bg-zinc-100'
    : 'bg-zinc-900 dark:bg-zinc-100';

  const inactiveBarClass = isChatMine
    ? 'bg-white/35'
    : isChatOther
    ? 'bg-zinc-300 dark:bg-zinc-600'
    : 'bg-zinc-300 dark:bg-zinc-600';

  const metaTextClass = isChatMine
    ? 'text-[9.5px] text-white/80'
    : isChatOther
    ? 'text-[9.5px] text-zinc-500 dark:text-zinc-400'
    : 'text-[11px] text-zinc-500 dark:text-zinc-400';

  const ringSize = isChat ? 34 : 46;
  const ringRadius = 15;
  const ringCirc = 2 * Math.PI * ringRadius;
  const knownProgress = typeof loadProgress === 'number';

  const buttonLabel = isUploading
    ? 'Uploading voice note'
    : isLoading
      ? 'Stop download'
      : (isPlaying ? 'Pause' : 'Play voice note');

  return (
    <div
      className={`${containerClasses} touch-manipulation`}
      onPointerUp={(e) => {
        if (e.pointerType !== 'touch') return;
        if (e.target.closest('[data-audio-ignore]') || e.target.closest('button')) return;
        e.preventDefault();
        e.stopPropagation();
        if (isLoading) return;
        if (!isPlaying) startPlayback();
        else if (!e.target.closest('[data-audio-scrub]')) togglePlay(e);
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (isLoading) return;
        if (e.target.closest('[data-audio-ignore]') || e.target.closest('button')) return;
        if (!isPlaying) startPlayback();
      }}
    >
      <audio
        ref={audioRef}
        playsInline
        preload="none"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <button
        type="button"
        onClick={togglePlay}
        disabled={isUploading}
        className={`relative ${playBtnClasses} ${isUploading ? 'opacity-85 cursor-wait' : ''}`}
        aria-label={buttonLabel}
      >
        {isLoading && (
          <svg
            className="absolute pointer-events-none"
            width={ringSize}
            height={ringSize}
            viewBox="0 0 36 36"
            aria-hidden="true"
            style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)' }}
          >
            <circle cx="18" cy="18" r={ringRadius} fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
            {knownProgress ? (
              <circle
                cx="18"
                cy="18"
                r={ringRadius}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${Math.max(0.08, downloadFraction) * ringCirc} ${ringCirc}`}
              />
            ) : (
              <circle
                cx="18"
                cy="18"
                r={ringRadius}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${ringCirc * 0.28} ${ringCirc}`}
                className="origin-center animate-spin"
              />
            )}
          </svg>
        )}
        {isUploading ? (
          <i className={`fas fa-circle-notch animate-spin ${isChat ? 'text-[11px]' : 'text-sm'}`} />
        ) : isLoading ? (
          <i className={`fas fa-stop ${isChat ? 'text-[9px]' : 'text-xs'}`} />
        ) : (
          <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play ml-0.5'} ${isChat ? 'text-[11px]' : 'text-sm'}`} />
        )}
      </button>

      <div className="flex-1 flex flex-col justify-center min-w-0">
        <div className={`flex items-center gap-[2px] ${isUploading || isLoading ? 'pointer-events-none' : 'cursor-pointer'} ${isChat ? 'h-5 py-0.5' : 'h-8 py-1'}`}>
          {bars.map((heightPct, idx) => {
            const barFraction = idx / bars.length;
            const isPlayed = barFraction <= progressFraction;

            return (
              <div
                key={idx}
                data-audio-scrub=""
                onClick={(e) => handleScrub(e, idx)}
                className="flex-1 h-full flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <div
                  className={`w-full rounded-full transition-all duration-100 ${
                    isPlayed ? activeBarClass : inactiveBarClass
                  }`}
                  style={{ height: `${heightPct}%`, minWidth: '1.5px' }}
                />
              </div>
            );
          })}
        </div>

        <div className={`flex items-center justify-between font-mono ${metaTextClass}`}>
          {isUploading ? (
            <span className="flex items-center gap-1.5 text-[10px] text-sky-200 dark:text-sky-300 font-sans font-medium select-none">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-ping" />
              <span>Uploading{typeof uploadProgress === 'number' ? ` ${Math.round(uploadProgress)}%` : '...'}</span>
            </span>
          ) : isLoading ? (
            <span className="font-sans font-medium">
              {knownProgress ? `${Math.round(downloadFraction * 100)}%` : 'Loading...'}
            </span>
          ) : (
            <span dir="ltr">{formatTime(currentTime)}</span>
          )}
          {!isChat && !isUploading && !isLoading && (
            <span className="flex items-center gap-1 font-sans text-[9.5px]">
              <i className="fas fa-microphone text-[8.5px] opacity-75" />
              Voice Note
            </span>
          )}
          <span dir="ltr">{formatTime(validDuration)}</span>
        </div>
      </div>

      <button
        type="button"
        data-audio-ignore=""
        onClick={handleSpeedToggle}
        className={`rounded-md font-bold transition-colors shrink-0 ${
          isChat
            ? isChatMine
              ? 'px-1 py-0.5 text-[9px] text-white/90 hover:bg-white/20'
              : 'px-1 py-0.5 text-[9px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700'
            : 'px-2 py-1 text-xs text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
        }`}
        title="Playback Speed"
      >
        {SPEEDS[speedIndex]}x
      </button>
    </div>
  );
};

export default AudioPlayer;
